import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, PackageCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSEO } from "@/lib/useSEO";

export default function TrackOrder() {
  useSEO({
    title: "Track Order — Thesis Printing & Academic Services · ScholarPrintHub",
    description: "Track your thesis printing or academic service order by order number or mobile. See real-time status: quote, work started, printing, binding, packed, shipped, delivered.",
    keywords: "track thesis printing order, order tracking thesis, courier tracking thesis print, scholarprinthub track",
  });
  const [orderNo, setOrderNo] = useState("");
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState(null);

  const search = async (e) => {
    e.preventDefault();
    if (!orderNo && !mobile) {
      toast.error("Enter order number or mobile");
      return;
    }
    setLoading(true);
    try {
      const params = {};
      if (orderNo) params.order_number = orderNo;
      if (mobile) params.mobile = mobile;
      const { data } = await api.get("/orders/track", { params });
      setOrders(data);
      if (!data.length) toast.info("No orders found. Please check the details.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="track-page" className="container-app py-16">
      <span className="section-eyebrow">Order tracking</span>
      <div className="divider-gold mt-2 mb-4" />
      <h1 className="font-display text-4xl sm:text-5xl text-navy">Track your order</h1>
      <p className="mt-3 text-slate-600 max-w-2xl">Enter your order ID (e.g. <span className="kbd">PR-260204-A1B2C3</span>) or mobile number to check status.</p>

      <form onSubmit={search} className="mt-10 border border-slate-200 rounded-sm p-6 bg-white grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-slate-600">Order number</Label>
          <Input data-testid="track-order-number" value={orderNo} onChange={(e) => setOrderNo(e.target.value.toUpperCase())} placeholder="PR-XXXXXX or AC-XXXXXX" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">or Mobile number</Label>
          <Input data-testid="track-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="10-digit mobile" />
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <button type="submit" data-testid="track-search-btn" disabled={loading} className="btn-primary">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
          </button>
        </div>
      </form>

      {orders && (
        <div className="mt-10 space-y-6">
          {orders.length === 0 && (
            <div className="border border-dashed border-slate-300 p-8 text-center text-slate-500 text-sm">No orders found.</div>
          )}
          {orders.map((o) => (
            <div key={o.id} data-testid={`track-order-${o.order_number}`} className="border border-slate-200 rounded-sm p-6 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="section-eyebrow">{o.type === "print" ? "Printing" : "Academic"} order</div>
                  <div className="font-display text-2xl text-navy mt-1">{o.order_number}</div>
                  <div className="text-xs text-slate-500 mt-1">Placed {new Date(o.created_at).toLocaleString("en-IN")}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">Current status</div>
                  <div className="font-medium text-navy flex items-center gap-2"><PackageCheck className="w-4 h-4 text-gold" /> {o.status}</div>
                  <div className="text-xs text-slate-500 mt-1">Payment: {o.payment_status}</div>
                  {o.quotation ? <div className="text-sm font-mono text-royal mt-1">Quote: ₹{o.quotation}</div> : null}
                </div>
              </div>

              <ol className="mt-6 relative border-l border-slate-200 ml-2">
                {(o.timeline || []).map((t, i) => (
                  <li key={i} className="ml-6 mb-4">
                    <span className="absolute -left-1.5 w-3 h-3 bg-gold rounded-full border border-navy" />
                    <div className="text-sm text-navy font-medium">{t.status}</div>
                    <div className="text-xs text-slate-500">{new Date(t.at).toLocaleString("en-IN")} · {t.note}</div>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
